// DAY-DIRECTSTAFF-2026-11-08 §1: "Ready to present" on Today -- the candidates the hub's profile_ready_unpresented view lists
// (submission-ready, never presented to a facility), with the open portal requests beside them, and the request the Present panel
// chooses first when opened from Today. A request for another discipline is said, never hidden: whether to present is staff's call.
// Ids, disciplines and states only.
import { cardDiscipline } from './workView';
import { COMPACT_STATES } from '../generated/compact';   // DAY-DIRECTSTAFF-2026-11-13 §1

const s = (v) => String(v == null ? '' : v).trim();
const isTest = (c) => !c || (Array.isArray(c.tags) && c.tags.some((x) => s(x).toLowerCase() === 'test')) || !!c.merged_into || !!c.test_marked_at;

/**
 * rows: profile_ready_unpresented ({ candidate_id, discipline, ready_since, days_ready }), null = the view is not readable;
 * candidates; waiting: the portal requests waiting (lib/facilityPortal.js waitingFromRows: { id, discipline, state, facility })
 * -> [{ candidate, id, discipline, days, since, requests: [{ id, facility, discipline, state, fits }] }], longest ready first
 */
export function readyToPresent({ rows = null, candidates = [], waiting = [] }) {
  const byId = new Map((candidates || []).map((c) => [String(c.id), c]));
  const out = [];
  for (const r of rows || []) {
    const c = byId.get(String(r.candidate_id));
    if (isTest(c)) continue;
    const discipline = cardDiscipline(c, { discipline: r.discipline });
    const requests = (waiting || []).map((q) => {
      const d = s(q.discipline).toUpperCase();
      return { id: Number(q.id), facility: s(q.facility) || 'A facility', discipline: d, state: s(q.state).toUpperCase(), fits: !d || !discipline || d === discipline };
    }).sort((a, b) => a.id - b.id);
    out.push({ candidate: c, id: Number(r.candidate_id), discipline, days: Number(r.days_ready) || 0, since: s(r.ready_since), requests });
  }
  return out.sort((a, b) => b.days - a.days || a.id - b.id);
}

/** "Ready to present: candidate 81 · PTA · ready 2 days · not presented to any facility" */
export const readyLine = (x) => `Ready to present: candidate ${x.id}${x.discipline ? ` · ${x.discipline}` : ''} · ready ${x.days} day${x.days === 1 ? '' : 's'} · not presented to any facility`;

/** "Open request: PlayDate #6 asks for PT, not PTA" | "Open requests: A #6 (PTA, GA) fits · B #7 asks for OT, not PTA" | "No open portal request right now" */
export function fitLine(x) {
  const reqs = (x && x.requests) || [];
  if (!reqs.length) return 'No open portal request right now';
  const one = (q) => (q.fits ? `${q.facility} #${q.id} (${[q.discipline || 'any discipline', q.state].filter(Boolean).join(', ')}) fits`
    : `${q.facility} #${q.id} asks for ${q.discipline}, not ${x.discipline}`);
  return `Open request${reqs.length === 1 ? '' : 's'}: ${reqs.map(one).join(' · ')}`;
}

// DAY-DIRECTSTAFF-2026-11-11 §3 (the hub: 81 is a PTA in TN, #6 a PT request in CO): a request MATCHES a candidate when its discipline is
// theirs (or none is given) and its state is one they can work (lib/jobsMatch.js statesOf: licences, a compact, else their home
// state; or the request names none). Test requests never match. The others stay one tap away ("Show all"), never chosen for them.
const order = (a, b) => (a.kind === b.kind ? (a.kind === 'request' ? a.id - b.id : 0) : a.kind === 'request' ? -1 : 1);
/** targets (lib/workView.js presentTargets), their discipline, their states -> { match, other }, portal requests first, oldest first */
export function splitTargets(targets = [], discipline = '', states = []) {
  const d = s(discipline).toUpperCase(); const st = new Set((states || []).map((x) => s(x).toUpperCase()));
  const fits = (t) => !t.test && (!t.discipline || !d || t.discipline === d) && (!t.state || st.has(t.state));
  const sorted = [...(targets || [])].sort(order);
  return { match: sorted.filter(fits), other: sorted.filter((t) => !fits(t)) };
}
/** where they can work, in words: "TN" | "GA or a compact state" (a licence in a compact state opens the rest) | '' -- from statesOf */
export function statesText(so) {
  const states = (so && so.states) || []; const why = (so && so.why) || {};
  if (!so || !so.compactVia) return states.join(', ');
  const own = states.filter((k) => !why[k] || why[k].kind !== 'compact');
  return `${own.join(', ')} or a compact state`;
}
/** "No open request matches PTA in TN." */
export const noMatchLine = (discipline, where = '') => `No open request matches ${s(discipline) || 'their discipline'}${where ? ` in ${where}` : ''}.`;
/** The request chosen first: the oldest that matches (splitTargets) -- else none: a request for another discipline or state is never
 *  chosen for them (11-08 chose the oldest open one with a warning). */
export function autoTarget(targets = [], discipline = '', states = null) {
  const pool = states === null ? (targets || []).filter((t) => !t.test && (!t.discipline || !discipline || t.discipline === s(discipline).toUpperCase())) : splitTargets(targets, discipline, states).match;
  const pick = [...pool].sort(order)[0];
  return pick ? pick.key : '';
}
// DAY-DIRECTSTAFF-2026-11-13 §1 (the hub: 81, a PTA in TN, ready, and no request it fits): the open requests they COULD fit if licensed --
// their discipline, in a state they cannot work yet; a state in their discipline's compact is flagged (a privilege may be quicker than a
// licence). Test requests never. -> [{ ...target, compact }] portal requests first, oldest first
export function licenceTargets(targets = [], discipline = '', states = []) {
  const d = s(discipline).toUpperCase(); const st = new Set((states || []).map((x) => s(x).toUpperCase()));
  const compact = new Set(COMPACT_STATES[d] || []);
  return [...(targets || [])].filter((t) => !t.test && d && t.discipline === d && t.state && !st.has(t.state)).sort(order)
    .map((t) => ({ ...t, compact: compact.has(t.state) }));
}
export const LICENCE_REASON = 'licence_question';
/** the hand-off's detail, ids only -- the candidate (and their lead, so People waiting can open and call), the state, the request */
export const licenceAskDetail = ({ candidateId, leadId = '', state, target, by = '' }) =>
  `Ask candidate ${candidateId} about a licence in ${state} for ${target.kind === 'request' ? `facility request ${target.id}` : `website facility request ${s(target.id).slice(0, 8)}`}`
  + ` (${target.discipline})${target.compact ? ' -- a compact state' : ''} · candidate_id=${candidateId}${leadId ? ` · lead_id=${leadId}` : ''}${by ? ` · asked by ${by}` : ''}`;
/** an open ask already there for this candidate and state? (notifications: the app's agent_notifications) */
export const openLicenceAsk = (notifications = [], candidateId, state) => (notifications || []).find((n) => n && n.reason === LICENCE_REASON && !n.handled_at
  && String(n.status || 'open') === 'open' && new RegExp(`candidate_id=${Number(candidateId)}(\\D|$)`).test(String(n.detail || '')) && new RegExp(` in ${s(state)} for `).test(String(n.detail || ''))) || null;

/** '' | "Request 12 is in CO; they can work in TN." (where: statesText) */
export function stateWarning(target, states = [], where = '') {
  const st = s(target && target.state).toUpperCase();
  if (!target || !st || (states || []).map((x) => s(x).toUpperCase()).includes(st)) return '';
  const which = target.kind === 'request' ? `Request ${target.id}` : `Website request ${s(target.id).slice(0, 8)}`;
  return `${which} is in ${st}; ${where || (states || []).join(', ') ? `they can work in ${where || states.join(', ')}` : 'no state is on file for them'}.`;
}

/** '' | "Request 6 asks for PT; this card says PTA. Present only if the facility said a PTA would do." */
export function disciplineWarning(target, discipline) {
  const want = s(target && target.discipline).toUpperCase(); const have = s(discipline).toUpperCase();
  if (!target || !want || !have || want === have) return '';
  const which = target.kind === 'request' ? `Request ${target.id}` : `Website request ${s(target.id).slice(0, 8)}`;
  return `${which} asks for ${want}; this card says ${have}. Present only if the facility said a${/^[AEFHILMNORSX]/.test(have) ? 'n' : ''} ${have} would do.`;
}
